<template>
  <div class="container-fluid py-4">

    <!-- ================= LOADER GLOBAL ================= -->
    <div v-if="isPageLoading" class="overlay">
      <div class="spinner-border text-success" role="status">
        <span class="visually-hidden">Chargement...</span>
      </div>
    </div>

    <!-- ================= HEADER ================= -->
    <div
      v-if="!isPageLoading"
      class="d-flex justify-content-between align-items-center mb-4 header-boutique"
    >

      <div class="d-flex align-items-center gap-3">

        <i class="bi bi-pencil-square fs-2 text-success"></i>

        <div>
          <h2 class="fw-bold mb-0">
            Modifier Configuration Boutique
          </h2>

          <small class="text-header">
            Mise à jour des paramètres de la Boutique 
          </small>
        </div>

      </div>

      <router-link
        class="btn btn-outline-success btn-lg shadow-sm btn-boutique"
        :to="{ name: 'item_config' }"
      >
        <i class="bi bi-arrow-left-circle me-2"></i>
        Retour
      </router-link>

    </div>

    <!-- ================= CARD ================= -->
    <div
      v-if="!isPageLoading"
      class="card-boutique p-4 shadow-sm rounded"
    >

      <!-- SECTION TITLE -->
      <div class="section-title mb-4">
        <i class="bi bi-shop"></i>
        <span>Informations de la boutique</span>
      </div>

      <!-- FORM -->
      <div class="row g-4">

        <!-- NOM -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">Nom de la boutique</label>

          <input
            type="text"
            v-model="config.nom"
            class="form-control"
            :class="{ 'is-invalid': error.nom }"
          />

          <div class="invalid-feedback">
            {{ error.nom }}
          </div>
        </div>

        <!-- TELEPHONE -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">Téléphone</label>

          <input
            type="text"
            v-model="config.telephone"
            class="form-control"
            :class="{ 'is-invalid': error.telephone }"
          />

          <div class="invalid-feedback">
            {{ error.telephone }}
          </div>
        </div>

        <!-- VILLE -->
        <div class="col-md-12">
          <label class="form-label fw-semibold">Ville</label>

          <input
            type="text"
            v-model="config.ville"
            class="form-control"
            :class="{ 'is-invalid': error.ville }"
          />

          <div class="invalid-feedback">
            {{ error.ville }}
          </div>
        </div>

        <!-- ADRESSE -->
        <div class="col-md-12">
          <label class="form-label fw-semibold">Adresse</label>

          <input
            type="text"
            v-model="config.adresse"
            class="form-control"
            :class="{ 'is-invalid': error.adresse }"
          />

          <div class="invalid-feedback">
            {{ error.adresse }}
          </div>
        </div>

        <!-- DESCRIPTION -->
        <div class="col-md-12">
          <label class="form-label fw-semibold">Description</label>

          <textarea
            v-model="config.description"
            class="form-control"
            rows="3"
          ></textarea>
        </div>

        <!-- LOGO -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">Changer le logo</label>

          <input
            type="file"
            @change="handleFileUpload"
            class="form-control"
            accept="image/*"
          />

          <div v-if="config.logo" class="mt-2">
            <img
              :src="getLogoUrl(config.logo)"
              class="img-thumbnail logo-preview"
            />
          </div>
        </div>

      </div>

      <!-- ACTIONS -->
      <div class="d-flex justify-content-end gap-3 mt-4">

        <button
          class="btn btn-light border shadow-sm px-4 py-2"
          @click="cancel"
        >
          Annuler
        </button>

        <button
          class="btn btn-success btn-boutique px-4 py-2"
          :disabled="isSaving"
          @click="updateConfiguration"
        >
          <span
            v-if="isSaving"
            class="spinner-border spinner-border-sm me-2"
          ></span>

          <span v-if="isSaving">Mise à jour...</span>
          <span v-else>Mettre à jour</span>
        </button>

      </div>

    </div>

  </div>
</template>
<script>
import api from '@/api';

export default {
  data() {
    return {

      isPageLoading: true,
      isSaving: false,

      config: {
        id: null,
        nom: '',
        adresse: '',
        ville: '',
        telephone: '',
        description: '',
        logo: ''
      },

      logoFile: null,

      error: {
        nom: '',
        adresse: '',
        ville: '',
        telephone: ''
      }

    };
  },

  methods: {

    async loadConfiguration() {
      this.isPageLoading = true;

      try {
        const id = this.$route.params.id;
        const response = await api.getItem('configurations', id);

        this.config = response.data;

      } catch (err) {
        console.error(err);
        alert("Impossible de charger la configuration");
      } finally {
        this.isPageLoading = false;
      }
    },

    handleFileUpload(event) {
      this.logoFile = event.target.files[0];
    },

    getLogoUrl(path) {
      return path
        ? `${process.env.VUE_APP_API_BASE_URL}${path}`
        : '';
    },

    validateForm() {

      this.error = {
        nom: this.config.nom ? '' : 'Le nom est requis',
        ville: this.config.ville ? '' : 'La ville est requise',
        adresse: this.config.adresse ? '' : "L'adresse est requise",
        telephone: this.config.telephone ? '' : 'Le téléphone est requis'
      };

      return !Object.values(this.error).some(e => e !== '');
    },

    async updateConfiguration() {

      if (!this.validateForm() || this.isSaving) return;

      try {

        this.isSaving = true;

        let payload;
        let isMultipart = false;

        if (this.logoFile) {

          payload = new FormData();

          payload.append('nom', this.config.nom);
          payload.append('ville', this.config.ville);
          payload.append('adresse', this.config.adresse);
          payload.append('telephone', this.config.telephone);
          payload.append('description', this.config.description);
          payload.append('logoFile', this.logoFile);

          isMultipart = true;

        } else {

          payload = {
            nom: this.config.nom,
            ville: this.config.ville,
            adresse: this.config.adresse,
            telephone: this.config.telephone,
            description: this.config.description
          };

        }

        const response = await api.updateItem(
          'configurations',
          this.config.id,
          payload,
          isMultipart
        );

        this.config = response.data;

        this.logoFile = null;

        this.$router.push({ name: 'item_config' });

      } catch (error) {
        console.error(error);
        alert("Erreur lors de la mise à jour");
      } finally {
        this.isSaving = false;
      }
    },

    cancel() {
      this.$router.push({ name: 'item_config' });
    }

  },

  created() {
    this.loadConfiguration();
  }

};
</script>
<style scoped>

/* LOADER */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(255,255,255,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* HEADER */
.header-boutique {
  padding: 15px 20px;
  background: linear-gradient(135deg, #064e3b, #10b981);
  border-radius: 12px;
  color: white;
}

/* TEXT */
.text-header {
  color: rgba(255,255,255,0.75);
}

/* CARD */
.card-boutique {
  background: #f8fafd;
  border-radius: 12px;
  border: none;
}

/* INPUT */
.form-control {
  border-radius: 10px;
  border: 1px solid #d1e3f0;
  padding: 10px 14px;
}

.form-control:focus {
  border-color: #10b981;
  box-shadow: 0 0 6px rgba(16,185,129,0.2);
}

/* BUTTON */
.btn-boutique {
  background: linear-gradient(135deg,#064e3b,#10b981);
  border: none;
  color: white;
  border-radius: 10px;
}

/* SECTION */
.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  color: #064e3b;
}

/* LOGO */
.logo-preview {
  max-height: 100px;
  border-radius: 10px;
}

/* MOBILE */
@media (max-width: 768px) {
  .header-boutique {
    flex-direction: column;
    gap: 15px;
  }
}

</style>